import type { ReactNode } from "react";
import { Drawer } from "vaul";
import { Icon, ICONS } from "./Icon";

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /** Yopish tugmasini ko'rsatish (top-right 32px circle). */
  showClose?: boolean;
  /** Yopish tugmasi foni: rasm ustida bo'lsa "dark". */
  closeTone?: "light" | "dark";
  align?: "left" | "center";
  children?: ReactNode;
}

/**
 * Pastki sheet — Figma §5.7. vaul asosida (drag-to-dismiss + a11y).
 * Panel: oq fon, yuqori burchak r16, padding 24/16/16. Drag handle 32×5.
 */
export function BottomSheet({
  open,
  onOpenChange,
  title,
  description,
  showClose = true,
  closeTone = "light",
  align = "left",
  children,
}: BottomSheetProps) {
  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[200]" style={{ background: "rgba(0,0,0,0.5)" }} />
        <Drawer.Content
          aria-describedby={undefined}
          className="fixed inset-x-0 bottom-0 z-[201] mx-auto flex max-w-lg flex-col rounded-t-2xl outline-none"
          style={{
            background: "var(--c-surface)",
            // Sheet hech qachon ko'rinadigan viewport'dan oshib ketmasligi kerak.
            // Ba'zi qurilma/webview'larda layout viewport ko'rinadigan (visual)
            // viewport'dan baland bo'ladi (super-app pastki paneli, home-indicator
            // qoplamasi) — natijada `bottom:0` ga bog'langan sheet'ning pasti fold
            // ostida qolib, oxirgi element yarim kesilib ko'rinardi. Dinamik viewport
            // balandligiga bog'lab, tepada notch/status-bar uchun joy qoldiramiz;
            // kontent sig'masa ichki qism scroll bo'ladi.
            maxHeight: "calc(100dvh - max(env(safe-area-inset-top), 24px))",
          }}
        >
          {/* Drag handle 32×5 — doim tepada, fiksatsiya */}
          <div className="flex shrink-0 justify-center pt-3" aria-hidden>
            <div className="h-[5px] w-8 rounded-full" style={{ background: "var(--c-text-disabled)" }} />
          </div>

          {/* Sarlavha — fiksatsiya (ro'yxat scroll bo'lsa ham ko'rinib turadi) */}
          <div className="shrink-0 px-4 pt-3">
            <div className={`flex items-start gap-3 ${align === "center" ? "justify-center text-center" : "justify-between"}`}>
              <div className="min-w-0 flex-1">
                {title ? (
                  <Drawer.Title className="text-xl font-semibold" style={{ color: "var(--c-text)" }}>
                    {title}
                  </Drawer.Title>
                ) : (
                  <Drawer.Title className="sr-only">Sheet</Drawer.Title>
                )}
                {description && (
                  <Drawer.Description className="mt-1 text-sm font-medium" style={{ color: "var(--c-text-muted)" }}>
                    {description}
                  </Drawer.Description>
                )}
              </div>
              {showClose && align !== "center" && (
                <Drawer.Close
                  className="grid size-8 shrink-0 place-items-center rounded-full transition-transform active:scale-95"
                  style={{
                    background: closeTone === "dark" ? "var(--c-text)" : "var(--c-line)",
                    color: closeTone === "dark" ? "var(--c-surface)" : "var(--c-text)",
                  }}
                  aria-label="Yopish"
                >
                  <Icon name={ICONS.close} size={20} />
                </Drawer.Close>
              )}
            </div>
          </div>

          {/* Kontent — kerak bo'lsa scroll bo'ladigan qism. Pastki safe-area
              padding aynan shu yerda: oxirgi element to'liq ko'rinadi va home
              indicator / webview paneli ortida qolib ketmaydi. */}
          <div
            className="grow min-h-0 overflow-y-auto px-4 pt-4"
            style={{
              paddingBottom: "max(env(safe-area-inset-bottom), 16px)",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {children}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
